import type { ComponentType } from "react";

import type { AgentBlock, Mode } from "../api/types";

/*
  The block registry (BUILD-PLAN 2.11.5). The agent panel draws each block of a conversation through
  the component registered for its type. F01 registers text, questions, explanation, notice,
  demo_banner, point_to and note_offer (completion round, CP-11: drawn in every panel); the session
  screens register plan, cards and hand_over.
*/

export type BlockType =
  | "text"
  | "plan"
  | "questions"
  | "explanation"
  | "cards"
  | "point_to"
  | "hand_over"
  | "notice"
  | "demo_banner"
  | "note_offer";

/** The block type that goes with a name. */
export type BlockOf<T extends BlockType> = Extract<AgentBlock, { type?: T }>;

/** What a block renderer knows about where it is drawn. */
export interface BlockContext {
  conversationKey: string;
  mode: Mode;
  /** Who said it: "assistant" for the agent, "user" for the person. */
  role: string;
  turnId?: string | null;
  /** True while the words are still arriving. */
  live: boolean;
  /** The session this conversation belongs to, when it is a session's conversation. */
  sessionId: string | null;
  /** The conversation is the admin's, which has its own endpoints. */
  admin: boolean;
}

export interface BlockProps<B extends AgentBlock = AgentBlock> {
  block: B;
  context: BlockContext;
}

export type BlockRenderer<B extends AgentBlock = AgentBlock> = ComponentType<BlockProps<B>>;

const renderers = new Map<string, BlockRenderer<never>>();

/**
 * Register the component for a block type. A later registration for the same type replaces the
 * earlier one. Returns a function that takes the registration away again.
 */
export function registerBlockRenderer<T extends BlockType>(type: T, component: BlockRenderer<BlockOf<T>>): () => void {
  renderers.set(type, component as unknown as BlockRenderer<never>);
  return () => {
    if (renderers.get(type) === (component as unknown as BlockRenderer<never>)) renderers.delete(type);
  };
}

export function getBlockRenderer(type: string): BlockRenderer | undefined {
  return renderers.get(type) as BlockRenderer | undefined;
}

export function registeredBlockTypes(): string[] {
  return [...renderers.keys()].sort();
}

/** The type of a block. The server names it; a block without a name is told by its shape. */
export function blockType(block: AgentBlock): string {
  const named = (block as { type?: string }).type;
  if (named) return named;
  const shape = block as unknown as Record<string, unknown>;
  if ("offer_id" in shape) return "note_offer";
  if ("questions" in shape) return "questions";
  if ("plan" in shape) return "plan";
  if ("card_ids" in shape) return "cards";
  if ("hand_over_id" in shape) return "hand_over";
  if ("sentence" in shape) return "point_to";
  if ("subject" in shape) return "explanation";
  if ("tone" in shape) return "notice";
  if ("text" in shape) return "text";
  return "unknown";
}
